import './sentry';

import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConfirmDialog } from 'primereact/confirmdialog';
// Load self hosted font
import '@/styles/font.css';
// Toast notification style
import 'react-toastify/dist/ReactToastify.min.css';
// Load TailwindCSS
import '@/styles/tailwind.css';
import '@/styles/account-themes.css';
import { applyAccountTheme, getBootstrapAccountTheme } from 'app/utils/account-theme';

applyAccountTheme(getBootstrapAccountTheme());
// Load PrimeReact styles
import 'primereact/resources/themes/tailwind-light/theme.css';
import 'primereact/resources/primereact.min.css';
// Load PrimeFlex a PrimeReact utility classes
import '/node_modules/primeflex/primeflex.css';
// Global styles
import '@/styles/global-styles.css';

import { App } from 'app';
import { HelmetProvider } from 'react-helmet-async';
import { PrimeReactProvider } from 'primereact/api';
import { bootstrapAuthTokenFromUrl } from 'app/utils/auth-token-bootstrap';
import { isInIframe } from 'app/utils/fleet-embed';
import { redirectDriverPortalIfNeeded } from 'app/utils/redirect-driver-portal-if-needed';
import { store } from '../src/store/store';
import reportWebVitals from './reportWebVitals';

// Initialize languages
import './locales/i18n';

// Iframe / SSO: apply ?token= before routes render.
const tokenAppliedFromUrl = bootstrapAuthTokenFromUrl();

const rootElement = document.getElementById('root') as HTMLElement;
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

function hasSsoToken(): boolean {
  try {
    return Boolean(
      window.sessionStorage.getItem('authToken') ||
        window.localStorage.getItem('token'),
    );
  } catch {
    return false;
  }
}

function showNeutralBootLoader() {
  rootElement.innerHTML =
    '<div style="min-height:100vh;display:flex;align-items:center;justify-content:center;margin:0;font-family:\'DM Sans\', sans-serif;font-size:15px;color:#4b5563;background:#f9fafb">Loading…</div>';
}

function renderApp() {
  rootElement.innerHTML = '';
  const root = createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <Provider store={store}>
        <HelmetProvider>
          <PrimeReactProvider>
            <QueryClientProvider client={queryClient}>
              <ConfirmDialog />
              <App />
            </QueryClientProvider>
            <ToastContainer hideProgressBar={true} />
          </PrimeReactProvider>
        </HelmetProvider>
      </Provider>
    </React.StrictMode>,
  );
}

async function start() {
  // Loading… only for a fresh iframe SSO boot (?token= in an iframe).
  // Avoids false positives from IDE preview iframes or leftover storage.
  const showBootLoader = isInIframe() && tokenAppliedFromUrl;
  const runPreMountDriverCheck = hasSsoToken();

  if (!runPreMountDriverCheck) {
    renderApp();
    return;
  }

  if (showBootLoader) {
    showNeutralBootLoader();
  }

  const redirected = await redirectDriverPortalIfNeeded();
  if (redirected) {
    return;
  }

  renderApp();
}

void start();

// Hot reloadable translation json files
if ((import.meta as any).hot) {
  (import.meta as any).hot.accept(['./locales/i18n'], (module: any) => {
    // No need to render the App again because i18next works with the hooks
  });
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
